import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  selectMultiRoadStretches,
  unSelectMultiRoadStretches,
} from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";

// Utils
import { toLocaleString } from "../../../common-utils/string-utils/StringUtils";
import { SortingBtn } from "./SortingUtils";
import {
  getSelectedFreqOptLocations,
  getImpFilteredStretches,
} from "./CampaignPlanningUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import { ProgressBar as HeaderProgressBar } from "../../../components/progress-bar/ProgressBar";
import TableHeaders from "../../../components/table/TableHeaders";
import NoTableDataMessage from "../../../components/table/NoTableDataMessage";
import CheckBox from "../../../components/check-box/CheckBox";
import RoadStretchTableRow from "./RoadStretchTableRow";

// Table Headers
function tableHeaders(
  originalCityStretchesCount,
  selectedStretchesCount,
  requestRoadStretchSort,
  tgId,
  impFilteredStretches
) {
  return [
    {
      element: (
        <RoadStretchHeader
          originalCityStretchesCount={originalCityStretchesCount}
          selectedStretchesCount={selectedStretchesCount}
        />
      ),
    },
    {
      element: <OtsHeader targetGroupId={tgId} />,
    },
    {
      title: {
        displayName: "Avg. MOP",
        className: "col-2 align-middle",
      },
    },
    {
      element: (
        <ActionHeader
          requestRoadStretchSort={requestRoadStretchSort}
          impFilteredStretches={impFilteredStretches}
        />
      ),
    },
  ];
}

function RoadStretchHeader({
  originalCityStretchesCount,
  selectedStretchesCount,
}) {
  const stretchCountData = [
    { label: "Selected Stretches", count: selectedStretchesCount },
    { label: "City Total Stretches", count: originalCityStretchesCount },
  ];

  return (
    <th className="col-3 align-middle">
      {stretchCountData.map((data, i) => {
        const { label, count } = data || {};
        const formattedCount = toLocaleString(count) || 0;
        return <p key={i} className="mb-0">{`${label} (${formattedCount})`}</p>;
      })}
    </th>
  );
}

function OtsHeader({ targetGroupId }) {
  return (
    <th className="col-5 pr-4 align-middle">
      {targetGroupId && (
        <div className="row no-gutters align-items-center">
          <span className="col-5">Imp TG</span>
          <div className="col-7">
            <HeaderProgressBar className="bg-light-green" percentage={40} />
          </div>
        </div>
      )}

      <div className="row no-gutters align-items-center">
        <span className="col-5">Imp Gen</span>
        <div className="col-7">
          <HeaderProgressBar className="bg-light-blue" percentage={100} />
        </div>
      </div>
    </th>
  );
}

function checkTableAllStretchesSelected(
  stretchesIds = [],
  selectedStretchesIds = []
) {
  if (stretchesIds.length === 0 || selectedStretchesIds.length === 0) {
    return false;
  }

  const isIncludesAll = stretchesIds.every((id) =>
    selectedStretchesIds.includes(id)
  );

  return isIncludesAll;
}

// Stretches Select All Button
function StretchesSelectAllButton({ impFilteredStretches = [] }) {
  const dispatch = useDispatch();

  // selected stretches map
  const stretchSelectedMap = useSelector(
    (state) => state.planningRoadStretches.selectedStretches
  );

  // Convert impFilteredStretches array to a map
  // these are only stretches which have both "imp" and "tgImp"
  const impFilteredStretchesMap = {};
  impFilteredStretches.forEach((stretch) => {
    impFilteredStretchesMap[stretch.id] = stretch;
  });

  const impFilteredStretchIds = Object.keys(impFilteredStretchesMap);
  const selectedStretchesIds = Object.keys(stretchSelectedMap);

  // data
  const stretchesToUse = impFilteredStretchesMap;
  const stretchIdsToUse = impFilteredStretchIds;

  // checking all table stretches are selected or not
  const isTableAllStretchesSelected = checkTableAllStretchesSelected(
    stretchIdsToUse,
    selectedStretchesIds
  );

  function onCheckboxChange(e) {
    const { checked } = e.target || {};

    // multi un select stretches
    if (!checked) {
      dispatch(unSelectMultiRoadStretches(stretchesToUse));
      return;
    }

    // multi select stretches
    dispatch(selectMultiRoadStretches(stretchesToUse));
  }

  return (
    <div className="d-flex align-items-center justify-content-center">
      <span className="mr-2 ml-1">Select All</span>
      <CheckBox
        boxStyle="mb-0 p-0"
        inputBoxStyle="m-0"
        showLabel={false}
        checked={isTableAllStretchesSelected}
        disabled={stretchIdsToUse.length === 0}
        onChange={onCheckboxChange}
      />
    </div>
  );
}

function ActionHeader({ requestRoadStretchSort, impFilteredStretches }) {
  return (
    <th className="col-2 align-middle text-right">
      {/* select/unselect all */}
      <StretchesSelectAllButton impFilteredStretches={impFilteredStretches} />

      {/* sorting */}
      <SortingBtn requestRoadStretchSort={requestRoadStretchSort} />
    </th>
  );
}

function RoadStretchTableBody({ roadStretchData, baseOts }) {
  // Duration
  const duration = useSelector(
    (state) => state.campaignPlanning.durationInDays
  );

  // region data
  const regionsData = useSelector(
    (state) => state.campaignPlanning.regionsData
  );
  const { id: selectedCityId } = regionsData || {};

  // selectedTouchPoints
  const selectedTouchPoints = useSelector(
    (state) => state.campaignPlanning.selectedTouchPoints
  );

  // Selected Region & Zones Map
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );
  const selectedFOL = getSelectedFreqOptLocations(
    selectedRegionAndZonesMap,
    selectedCityId
  );

  // RoadStretch data is empty return null
  if (roadStretchData.length === 0) {
    return <NoTableDataMessage colSpan={4} message="No Stretches Present" />;
  }

  // Road stretch table row
  return (
    <>
      {roadStretchData.map((stretchInfo, index) => (
        <RoadStretchTableRow
          key={stretchInfo.id}
          index={index}
          stretchInfo={stretchInfo}
          dayCount={duration}
          baseOts={baseOts}
          selectedTouchPoints={selectedTouchPoints}
          selectedFOL={selectedFOL}
        />
      ))}
    </>
  );
}

/**
 * Road Stretch Table
 */
export default function RoadStretchTable({
  roadStretchData = [],
  baseOts,
  roadStretchLoading = false,
  regionsDataLoading = false,
  requestRoadStretchSort,
}) {
  // Get CityId once city is selected
  const cityId = useSelector((state) => state.campaignPlanning.cityId);

  // Selected Stretches
  const selectedStretchesMap = useSelector(
    (state) => state.planningRoadStretches.selectedStretches
  );
  const selectedStretchesCount = Object.keys(selectedStretchesMap).length;

  // original City Stretches Count
  const originalCityStretchesCount = useSelector(
    (state) => state.planningRoadStretches.originalCityStretchesCount
  );

  // targetGroupId
  const tgId = useSelector((state) => state.tgSpecificOts.tgId);

  // Loading
  if (roadStretchLoading || regionsDataLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-4">
        <Spinner />
        <span className="ml-2">Please wait, Road Stretches are Loading...</span>
      </div>
    );
  }

  if (!cityId) {
    return null;
  }

  // filter road stretches by imp/tgImp
  const impFilteredStretches = getImpFilteredStretches(roadStretchData, tgId);

  const stretchTableHeaders = tableHeaders(
    originalCityStretchesCount,
    selectedStretchesCount,
    requestRoadStretchSort,
    tgId,
    impFilteredStretches
  );

  // stretches table
  return (
    <table className="table">
      <TableHeaders
        tableHeaders={stretchTableHeaders}
        headerClass="bg-white"
        requiredSubHeader={false}
      />

      <tbody>
        <RoadStretchTableBody
          roadStretchData={impFilteredStretches}
          baseOts={baseOts}
          roadStretchLoading={roadStretchLoading}
        />
      </tbody>
    </table>
  );
}
